// Saved jobs, saved searches and approximate distances for the web pages.
// Distances only ever use postcode district centres (like "LS6"), never addresses.

// Web feed kinds ('job' | 'home_job' | 'tender') to the saved kinds the database uses.
export const savedKindFor = { job: 'commercial', home_job: 'home', tender: 'private_tender' };
export const savedKindLabels = { home: 'Residential', commercial: 'Business', private_tender: 'Private tender' };
export const distanceOptions = [5, 10, 25, 50];

const friendly = {
  SIGN_IN_REQUIRED: 'Sign in to save jobs and searches.',
  JOB_NOT_AVAILABLE: 'This job is no longer available.',
  SAVED_JOB_LIMIT: 'You have saved the most jobs you can. Remove some to save more.',
  SAVED_SEARCH_LIMIT: 'You have saved the most searches you can. Remove one to save another.',
  POSTCODE_AREA_INVALID: 'Enter a UK postcode or the first part of one, like LS6.',
  POSTCODE_AREA_UNKNOWN: "We couldn't find that postcode area.",
  INVALID_BUDGET_RANGE: 'The lowest budget must be less than the highest.',
};

export function savedErrorMessage(message, fallback = 'Something went wrong. Please try again.') {
  if (!message) return fallback;
  const code = Object.keys(friendly).find((key) => String(message).includes(key));
  return code ? friendly[code] : fallback;
}

function check(error, fallback) { if (error) throw new Error(savedErrorMessage(error.message, fallback)); }

/** "About 4 miles away", or null when there is no distance to show. */
export function formatDistance(miles) {
  if (miles === null || miles === undefined || miles === '' || !Number.isFinite(Number(miles))) return null;
  const value = Number(miles);
  if (value < 1) return 'Less than a mile away';
  const rounded = Math.round(value);
  return `About ${rounded} ${rounded === 1 ? 'mile' : 'miles'} away`;
}

/** The district part of a postcode ("LS6 2AB" -> "LS6"), or null. */
export function outcodeOf(postcode) {
  const compact = String(postcode ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const full = /^([A-Z]{1,2}[0-9][A-Z0-9]?)([0-9][A-Z]{2})$/.exec(compact);
  if (full) return full[1];
  return /^[A-Z]{1,2}[0-9][A-Z0-9]?$/.test(compact) ? compact : null;
}

/** The approximate centre of a postcode district. Only the district is sent. */
export async function geocodePostcode(postcode, fetchImpl = fetch) {
  const outcode = outcodeOf(postcode);
  if (!outcode) throw new Error('Enter a UK postcode or the first part of one, like LS6.');
  let response;
  try { response = await fetchImpl(`/.netlify/functions/geocode-postcode?postcode=${encodeURIComponent(outcode)}`); }
  catch { throw new Error('Could not reach Resident Service 360. Check your connection and try again.'); }
  const payload = await response.json().catch(() => ({}));
  if (response.status === 404 && payload.code === 'POSTCODE_AREA_NOT_FOUND') throw new Error("We couldn't find that postcode area.");
  if (!response.ok || typeof payload.lat !== 'number' || typeof payload.lng !== 'number') throw new Error('Distance search is not available right now. Please try again later.');
  return { outcode: payload.outcode, lat: payload.lat, lng: payload.lng };
}

// Saved keys are loaded once per signed-in person and shared by every bookmark button.
let cache = { userId: null, keys: new Set(), promise: null };
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());
export function subscribeSaved(listener) { listeners.add(listener); return () => listeners.delete(listener); }
export const isSaved = (kind, id) => cache.keys.has(`${kind}:${id}`);

export function loadSavedKeys(client, userId) {
  if (!client || !userId) { cache = { userId: null, keys: new Set(), promise: null }; emit(); return Promise.resolve(cache.keys); }
  if (cache.userId === userId && cache.promise) return cache.promise;
  cache = { userId, keys: new Set(), promise: null };
  cache.promise = Promise.resolve(client.from('saved_jobs').select('job_kind,job_id')).then(({ data, error }) => {
    if (error) return cache.keys;
    if (cache.userId === userId) { cache.keys = new Set((data || []).map((row) => `${row.job_kind}:${row.job_id}`)); emit(); }
    return cache.keys;
  }).catch(() => cache.keys);
  return cache.promise;
}

/** Saves or un-saves a job. Returns the new saved state. */
export async function toggleSavedJob(client, kind, id) {
  const key = `${kind}:${id}`;
  const was = cache.keys.has(key);
  const { error } = await client.rpc(was ? 'unsave_job' : 'save_job', { p_kind: kind, p_job_id: id });
  check(error, was ? 'Could not remove this job.' : 'Could not save this job.');
  cache.keys = new Set(cache.keys);
  if (was) cache.keys.delete(key); else cache.keys.add(key);
  emit();
  return !was;
}

export function resetSavedCache() { cache = { userId: null, keys: new Set(), promise: null }; emit(); }

export async function listSavedJobs(client, base = null) {
  const { data, error } = await client.rpc('list_saved_jobs', { p_base_lat: base?.lat ?? null, p_base_lng: base?.lng ?? null });
  check(error, 'Could not load your saved jobs.');
  return data || [];
}

export async function listSavedSearches(client) {
  const { data, error } = await client.from('saved_searches').select('*').order('created_at', { ascending: false });
  check(error, 'Could not load your saved searches.');
  return data || [];
}

/** Checks a new search before sending it. Only the district centre is stored. */
export function savedSearchRecord({ name, kinds, trade = '', keywords = '', base = null, maxMiles = null }) {
  const clean = String(name || '').trim();
  if (!clean) throw new Error('Give this search a name.');
  if (!kinds?.length) throw new Error('Choose at least one type of job.');
  if (maxMiles && !base) throw new Error('Enter a postcode to search by distance.');
  return {
    name: clean.slice(0, 80), kinds, trades: String(trade).trim() ? [String(trade).trim()] : [], keywords: String(keywords).trim(),
    base_outcode: base?.outcode ?? null, base_lat: base?.lat ?? null, base_lng: base?.lng ?? null, max_miles: base && maxMiles ? Number(maxMiles) : null,
    alerts_enabled: true,
  };
}

export async function createSavedSearch(client, input) {
  const { error } = await client.from('saved_searches').insert(savedSearchRecord(input));
  check(error, 'Could not save this search.');
}

export async function setSearchAlerts(client, id, enabled) {
  const { data, error } = await client.from('saved_searches').update({ alerts_enabled: enabled }).eq('id', id).select('id');
  check(error, 'Could not change alerts for this search.');
  if (!data?.length) throw new Error('Only the person who saved this search or a company admin can change it.');
}

export async function deleteSavedSearch(client, id) {
  const { data, error } = await client.from('saved_searches').delete().eq('id', id).select('id');
  check(error, 'Could not delete this search.');
  if (!data?.length) throw new Error('Only the person who saved this search or a company admin can delete it.');
}

/** What a saved search looks for, in plain words. */
export function describeSearch(search) {
  return [
    (search.kinds || []).map((kind) => savedKindLabels[kind]).join(', '),
    search.trades?.length ? search.trades.join(', ') : 'All trades',
    search.keywords ? `"${search.keywords}"` : '',
    search.base_outcode ? (search.max_miles ? `Within ${search.max_miles} miles of ${search.base_outcode}` : `Near ${search.base_outcode}`) : '',
  ].filter(Boolean).join(' · ');
}
